import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { createClient } from '@supabase/supabase-js'
import SalaVideo from './SalaVideo'

const API = process.env.REACT_APP_API_URL

function cabeceras(usuario) {
  return { headers: { 'x-usuario-id': usuario?.id } }
}

// Mismo patron que en el panel de admin: el navegador sube el archivo
// directamente a Supabase Storage (bucket "fotos-profesores") y solo
// la URL publica resultante se guarda en la base de datos.
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

async function subirFotoProfesor(archivo) {
  const ext = (archivo.name?.split('.').pop() || 'jpg').toLowerCase()
  const nombreArchivo = 'profesor_' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + ext
  const { error } = await supabase.storage
    .from('fotos-profesores')
    .upload(nombreArchivo, archivo, { contentType: archivo.type })
  if (error) throw error
  const { data } = supabase.storage.from('fotos-profesores').getPublicUrl(nombreArchivo)
  return data.publicUrl
}

// Tarjeta para que el profesor edite su propia foto y biografia. El
// nombre y el correo se muestran de solo lectura -- esos los controla
// unicamente el administrador.
function TarjetaMiPerfil({ usuario, perfil, onActualizado }) {
  const [editando, setEditando] = useState(false)
  const [bio, setBio] = useState(perfil.bio || '')
  const [subiendoFoto, setSubiendoFoto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  async function elegirFoto(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setSubiendoFoto(true)
    setError(null)
    try {
      const foto_url = await subirFotoProfesor(archivo)
      const r = await axios.put(`${API}/clases/profesor/${perfil.id}/mi-perfil`, { foto_url })
      onActualizado(r.data.datos)
    } catch (err) {
      setError('No se pudo subir la foto. Intenta de nuevo.')
    }
    setSubiendoFoto(false)
    e.target.value = ''
  }

  async function guardarBio() {
    setGuardando(true)
    setError(null)
    try {
      const r = await axios.put(`${API}/clases/profesor/${perfil.id}/mi-perfil`, { bio })
      onActualizado(r.data.datos)
      setEditando(false)
    } catch (err) {
      setError('No se pudo guardar. Intenta de nuevo.')
    }
    setGuardando(false)
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 18, marginBottom: 20, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#F4F6FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, overflow: 'hidden', flexShrink: 0 }}>
        {perfil.foto_url ? <img src={perfil.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (perfil.avatar_emoji || '🧑‍🏫')}
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <label style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 8, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>
          {subiendoFoto ? 'Subiendo...' : '📷 Cambiar mi foto'}
          <input type="file" accept="image/*" onChange={elegirFoto} disabled={subiendoFoto} style={{ display: 'none' }} />
        </label>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
          Correo: {usuario?.email} <span style={{ fontStyle: 'italic' }}>(no se puede cambiar aquí)</span>
        </div>
        {!editando ? (
          <div>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 8 }}>{perfil.bio || 'Todavía no has escrito una biografía.'}</p>
            <button onClick={() => { setBio(perfil.bio || ''); setEditando(true) }}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Editar biografía
            </button>
          </div>
        ) : (
          <div>
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Cuéntale al estudiante sobre ti..."
              style={{ width: '100%', minHeight: 70, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E6F0', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={guardarBio} disabled={guardando}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setEditando(false)}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
        {error && <p style={{ fontSize: 12, color: '#991B1B', marginTop: 8 }}>{error}</p>}
      </div>
    </div>
  )
}

function formatoFechaHora(fecha, hora) {
  if (!fecha) return ''
  const [y, m, d] = fecha.split('-').map(Number)
  const texto = `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
  return hora ? `${texto} · ${hora.slice(0, 5)}` : texto
}

const ROTULO_SALA = {
  inactiva: { texto: 'Sin iniciar', color: '#6B7280' },
  en_vivo: { texto: '● En vivo', color: '#166534' },
  finalizada: { texto: 'Finalizada', color: '#6B7280' }
}

// Panel para el rol "Profesor" (elegido en Login.jsx). El acceso real
// NO se decide por el rol que eligio en el login (eso solo elige a que
// pantalla ir) — se verifica aqui contra la base de datos, buscando si
// la cuenta esta vinculada a una fila de "profesores". Mismo patron de
// seguridad que PanelAdmin.jsx con usuarios.es_admin.
export default function PanelProfesor({ usuario, onLogout }) {
  const [estado, setEstado] = useState('verificando') // 'verificando' | 'autorizado' | 'denegado'
  const [perfil, setPerfil] = useState(null)
  const [clases, setClases] = useState([])
  const [cargandoClases, setCargandoClases] = useState(true)
  const [claseEnVideo, setClaseEnVideo] = useState(null)
  const [roster, setRoster] = useState(null) // { claseId, filas } | null

  useEffect(() => {
    if (!usuario?.id) { setEstado('denegado'); return }
    axios.get(`${API}/clases/profesor/mi-perfil/${usuario.id}`)
      .then(r => {
        const datos = r.data?.datos
        if (datos && datos.activo) { setPerfil(datos); setEstado('autorizado') }
        else setEstado('denegado')
      })
      .catch(() => setEstado('denegado'))
  }, [usuario])

  const cargarClases = useCallback(() => {
    if (!perfil?.id) return
    setCargandoClases(true)
    axios.get(`${API}/clases`, { params: { profesor_id: perfil.id } })
      .then(r => setClases(r.data.datos || []))
      .catch(() => {})
      .finally(() => setCargandoClases(false))
  }, [perfil])

  useEffect(() => { cargarClases() }, [cargarClases])

  async function verRoster(claseId) {
    setRoster({ claseId, filas: null })
    try {
      const r = await axios.get(`${API}/clases/${claseId}/roster`, cabeceras(usuario))
      setRoster({ claseId, filas: r.data.datos || [] })
    } catch {
      setRoster({ claseId, filas: [] })
    }
  }

  if (claseEnVideo) {
    return <SalaVideo usuario={usuario} claseId={claseEnVideo} onSalir={() => { setClaseEnVideo(null); cargarClases() }} />
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFF', fontFamily: 'system-ui, sans-serif' }}>
      <header style={{ background: 'linear-gradient(90deg, #059669, #047857)', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>👨‍🏫</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>EnglishBridge · Profesor</div>
            <div style={{ fontSize: 11, opacity: .7 }}>{usuario?.email}</div>
          </div>
        </div>
        <button onClick={onLogout}
          style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}>
          🚪 Cerrar sesion
        </button>
      </header>

      <div style={{ padding: '28px 20px', maxWidth: 900, margin: '0 auto' }}>
        {estado === 'verificando' && (
          <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>Verificando acceso...</div>
        )}

        {estado === 'denegado' && (
          <div style={{ maxWidth: 460, margin: '60px auto', textAlign: 'center', background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8, color: '#1A1A2E' }}>Tu cuenta todavía no está vinculada</h2>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
              Tu cuenta ({usuario?.email}) inició sesión como Profesor, pero un administrador todavía no la vinculó a ningún perfil de profesor. Pídele que lo haga desde el panel de administrador, pestaña "Profesores".
            </p>
            <button onClick={onLogout}
              style={{ padding: '10px 20px', background: '#059669', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Cerrar sesion
            </button>
          </div>
        )}

        {estado === 'autorizado' && (
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E', marginBottom: 6 }}>
              {perfil?.avatar_emoji} Hola, {perfil?.nombre}
            </h1>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
              Tus próximas clases. Puedes iniciar la videollamada, grabarla, y ver quién se reservó — sin necesitar acceso de administrador completo.
            </p>

            <TarjetaMiPerfil usuario={usuario} perfil={perfil} onActualizado={datos => setPerfil(p => ({ ...p, ...datos }))} />

            {cargandoClases && <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>}

            {!cargandoClases && clases.length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: '#6B7280', background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14 }}>
                No tienes clases próximas asignadas todavía.
              </div>
            )}

            {!cargandoClases && clases.map(c => (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 18, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{c.titulo || (c.tipo_clase === 'grupal' ? 'Clase grupal' : 'Clase privada')}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      {formatoFechaHora(c.fecha, c.hora_inicio)} · {c.tipo_clase === 'grupal' ? 'Grupal' : 'Privada'}
                      {c.nivel_id ? ` · ${c.nivel_id}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: (ROTULO_SALA[c.sala_estado] || ROTULO_SALA.inactiva).color }}>
                      {(ROTULO_SALA[c.sala_estado] || ROTULO_SALA.inactiva).texto}
                    </span>
                    <button onClick={() => verRoster(c.id)}
                      style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      👥 Ver reservas
                    </button>
                    <button onClick={() => setClaseEnVideo(c.id)}
                      style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      🎥 {c.sala_estado === 'en_vivo' ? 'Entrar' : 'Iniciar clase'}
                    </button>
                  </div>
                </div>

                {roster?.claseId === c.id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E2E6F0' }}>
                    {roster.filas === null && <div style={{ fontSize: 12, color: '#6B7280' }}>Cargando reservas...</div>}
                    {roster.filas?.length === 0 && <div style={{ fontSize: 12, color: '#6B7280' }}>Nadie se ha reservado todavía.</div>}
                    {roster.filas && roster.filas.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                        {roster.filas.map((f, i) => <li key={i}>{f.email || f.usuario_id}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
